import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

/**
 * Attaches the Cognito ID token to outbound requests. The BFF exchanges it for
 * the SRD authn-service token per request, so the client only ever holds the
 * Cognito token. On a 401/403 the Cognito session is refreshed and the request
 * is retried once before falling back to re-login.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  const token = authService.getToken();

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 403) {
        // Attempt to refresh the Cognito session and retry once
        return from(authService.refreshToken()).pipe(
          switchMap((newToken) => {
            if (newToken) {
              const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next(retryReq);
            }
            // refreshToken already redirected to Cognito if session expired
            return throwError(() => error);
          }),
          catchError(() => {
            // If refresh also fails, redirect to login
            authService.redirectToCognito();
            return throwError(() => error);
          }),
        );
      } else if (error.status >= 500) {
        toastService.show(
          'Something went wrong. Please try again.',
          'error',
          { retry: true },
        );
      } else if (error.status >= 400) {
        const message = error.error?.message || error.message || 'An error occurred.';
        toastService.show(message, 'error');
      }

      return throwError(() => error);
    }),
  );
};
